import { Coordinate } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { COORDINATES_PER_CALL } from "@/hooks/addresses/useAddresses";
import { chunkArray } from "@/utilities/chunkArray";

/** Address chunks requested at the same time. */
export const GEOCODE_CHUNKS_IN_FLIGHT = 4;

/** Decimal places kept when coordinates are deduplicated: 5 places is about one metre. */
const COORDINATE_DECIMALS = 5;

/** The dedupe key of a coordinate: positions within about a metre share one address lookup. */
export function coordinateKey(point?: unknown): string | undefined {
  const coordinate = point as Coordinate | undefined;
  if (!coordinate) return undefined;
  return `${coordinate.x.toFixed(COORDINATE_DECIMALS)},${coordinate.y.toFixed(COORDINATE_DECIMALS)}`;
}

/** FNV-1a, enough to tell coordinate sets apart in a query key without hashing the whole array each render. */
function hashKeys(keys: string[]): string {
  let hash = 0x811c9dc5;
  for (const key of keys) {
    for (let i = 0; i < key.length; i++) {
      hash ^= key.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    hash ^= 0x7c;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Reverse-geocodes deduplicated coordinates in chunks of {@link COORDINATES_PER_CALL}. Addresses
 * already resolved stay cached for the session, so widening the set (the export asks for every
 * row after the visible ones were resolved) fetches only what is new.
 *
 * The query key is the count plus a hash of the coordinate keys, not the array itself, so a
 * fleet-sized set is not re-serialised by React Query on every render.
 *
 * `data` maps coordinate key to address ("" when Geotab has none). Like `useQuery` with
 * `keepPreviousData`, `isPlaceholderData` is true while the data belongs to a previous set.
 */
export function useCoordinateAddresses(coordinatesByKey: Map<string, Coordinate>) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const resolved = useRef<{ session: string | null; byKey: Map<string, string> }>({
    session: activeSessionId,
    byKey: new Map(),
  });
  if (resolved.current.session !== activeSessionId) {
    resolved.current = { session: activeSessionId, byKey: new Map() };
  }

  // Sorted and hashed once per set: a fleet-month export holds hundreds of thousands of keys and
  // the page renders on every hover and keystroke.
  const keys = useMemo(() => [...coordinatesByKey.keys()].sort(), [coordinatesByKey]);
  const keysHash = useMemo(() => hashKeys(keys), [keys]);

  return useQuery({
    queryKey: ["coordinateAddresses", activeSessionId, keys.length, keysHash],
    queryFn: async ({ signal }) => {
      const cache = resolved.current.byKey;
      const missing = keys.filter(key => !cache.has(key));
      const resolveChunk = async (chunk: string[]) => {
        const addresses = await geotabClient.getAddresses(
          chunk.map(key => coordinatesByKey.get(key) as Coordinate),
          false,
          false,
          signal,
        );
        // Paired back by index: the response carries no coordinate.
        chunk.forEach((key, index) => cache.set(key, addresses?.[index]?.formattedAddress ?? ""));
      };

      // A few chunks at a time: the client batches queued calls into one request, and one failed
      // call fails the others in that request, so all chunks at once would fail together.
      const chunks = chunkArray(missing, COORDINATES_PER_CALL);
      const failed: string[][] = [];
      let next = 0;
      await Promise.all(
        Array.from({ length: Math.min(GEOCODE_CHUNKS_IN_FLIGHT, chunks.length) }, async () => {
          while (next < chunks.length) {
            const chunk = chunks[next++];
            try {
              await resolveChunk(chunk);
            } catch {
              failed.push(chunk);
            }
          }
        }),
      );
      // A failed chunk is retried once on its own; if it still fails, the error is surfaced and
      // the chunks that did resolve stay cached.
      for (const chunk of failed) {
        signal.throwIfAborted();
        await resolveChunk(chunk);
      }
      return new Map(keys.map(key => [key, cache.get(key) ?? ""]));
    },
    placeholderData: keepPreviousData,
  });
}
